import React, { createContext, useContext, useCallback, ReactNode, useState, useEffect, useMemo, useRef } from 'react';
import * as strings from 'layouts/user/Research/strings';
import {
  ChatMessage,
  CheckboxState,
  ResearchContextState,
  ResearchContextActions
} from 'models/user/research';
import ClipboardService from 'services/ClipboardService';
import TokenService from 'services/TokenService';
import { getWindowLocation } from 'utils/location.utils';
import { pushToastMessage } from 'store/actions/root/toast';
import { useDispatch } from 'react-redux';
import { mdToHTML } from 'utils/MD.utils';
import {
  isCategorizedFollowUpQuestions,
  FollowUpQuestionContent,
  convertFlatArrayToFollowUpQuestions
} from 'models/user/research/FollowUpQuestion.model';
import { logWhenInDevMode } from 'utils/debug';

function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const HOST = process.env.REACT_APP_SERVER;

// Auth pilot (huseyin_works/tasks/2026-08-05_zephy_backend_auth_gap/task_latest.md, Task 4):
// sends the same session token TokenService already tracks (set at login, see
// LoginForm.jsx), as a real Authorization header this time - the existing `addToken: true`
// mechanism (HttpInterseptor.js) puts it in the URL query string or body instead, which
// verifyUserToken.js on the backend doesn't read.
export function authHeader(): Record<string, string> {
  const token = new TokenService().getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const COPY_LINK_TOAST_DATA = {
  title: "Share link is copied to clipboard",
  iconName: "content_copy",
  iconColor: "green-light",
  duration: 3000,
};

const ResearchContext = createContext<{
  state: ResearchContextState;
  actions: ResearchContextActions;
} | undefined>(undefined);

const ResearchContextProviderComponent: React.FC<{ children: ReactNode; }> = ({
  children,
}) => {
  const latestControllerRef = useRef<AbortController | null>(null);
  const latestFetchInputSuggestionsControllerRef = useRef<AbortController | null>(null);
  const latestFetchEnhanceSuggestionsControllerRef = useRef<AbortController | null>(null);
  const stopRef = useRef<boolean>(false);



  const dispatch = useDispatch();

  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [isFuqLoading, setFUQLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [latestMessage, setLatestMessage] = useState<string>('');
  const [model, setModel] = useState<string>('');
  const [longReportType, setLongReportType] = useState<string>('researchReport');
  const lockedDateTypes: string[] = ['swotReport', 'pestlReport']; // SWOT ve PESTL kilitli
  const isDateLocked = lockedDateTypes.includes(longReportType);
  const [typing, setTyping] = useState<boolean>(false);
  const [streaming, setStreaming] = useState<boolean>(false);
  const [expandingTyping, setExpandingTyping] = useState<boolean>(false);
  const [stop, setStop] = useState<boolean>(false);

  // Update stop ref when stop state changes
  useEffect(() => {
    stopRef.current = stop;
  }, [stop]);
  const [autocomplete, setAutocomplete] = useState<boolean>(true);
  const [showConversationHistory, setShowConversationHistory] = useState<boolean>(false);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [showMoreClicked, setShowMoreClicked] = useState<boolean>(false);
  const [diveDeeperClicked, setDiveDeeperClicked] = useState<boolean>(false);
  const [imagesLoading, setImagesLoading] = useState<boolean>(false);
  const [images, setImages] = useState<Record<string, string>>({});
  const [presentationStatusMessage, setPresentationStatusMessage] = useState<string>('');
  const [conversationHistory, setConversationHistory] = useState<any[]>([]);
  const [followUpQuestions, setFollowUpQuestions] = useState<FollowUpQuestionContent>({ followUpQuestions: [{ category: 'General', questions: strings.followUpQuestions }] });
  const [inputSuggestions, setInputSuggestions] = useState<string[]>([]);
  const [enhanceSuggestions, setEnhanceSuggestions] = useState<string[]>([]);
  const [showEnhanceOptions, setShowEnhanceOptions] = useState<boolean>(false);
  const [enhanceLoading, setEnhanceLoading] = useState<boolean>(false);
  const [timestamp, setTimestamp] = useState<number>(Date.now());
  const [error, setError] = useState<string | null>(null);
  const [sendUserMessage, setSendUserMessage] = useState<() => void>(() => () => { });
  const [onExpandResearchClick, setOnExpandResearchClick] = useState<(researchType: string) => void>(() => (researchType: string) => { });
  const [controller, setController] = useState<AbortController | null>(null);
  const [fetchInputSuggestionsController, setFetchInputSuggestionsController] = useState<AbortController | null>(null);
  const [fetchEnhanceSuggestionsController, setFetchEnhanceSuggestionsController] = useState<AbortController | null>(null);
  const [routerReportType, setRouterReportType] = useState<string>('');

  const [myDataCheckBox, setMyDataCheckBox] = useState<CheckboxState>({ id: 'myData', text: 'My Data', value: false, color: 'blue' });
  const [liteDataCheckBox, setLiteDataCheckBox] = useState<CheckboxState>({ id: 'liteData', text: 'Lite Data', value: true, color: 'green' });
  const [longReportCheckBox, setLongReportCheckBox] = useState<CheckboxState>({ id: 'longReport', text: 'Long Report', value: false, color: 'purple' });
  const [shortReportCheckBox, setShortReportCheckBox] = useState<CheckboxState>({ id: 'shortReport', text: 'Short Report', value: true, color: 'orange' });
  const [modelDataCheckBox, setModelDataCheckBox] = useState<CheckboxState>({ id: 'modelData', text: 'Model Data', value: false, color: 'red' });
  const [customReportCheckBox, setCustomReportCheckBox] = useState<CheckboxState>({ id: 'customReport', text: 'Custom Report', value: false, color: 'indigo' });

  const [startMyDataDate, setStartMyDataDate] = useState<string>('2020-01-01');
  const [endMyDataDate, setEndMyDataDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [startLiteDataDate, setStartLiteDataDate] = useState<string>('2020-01-01');
  const [endLiteDataDate, setEndLiteDataDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [userDateOverride, setUserDateOverride] = useState<boolean>(false);

  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const [reportPrompt, setReportPrompt] = useState<string>('');
  const [keywordPrompt, setKeywordPrompt] = useState<string>('');

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [searchFilters, setSearchFilters] = useState<Record<string, any>>({});

  const [defaultFollowUpQuestions, setDefaultFollowUpQuestions] = useState<Record<string, string[]>>(strings.defaultFollowUpQuestions);
  const [reportFollowUpQuestions, setReportFollowUpQuestions] = useState<Record<string, string[]>>(strings.reportFollowUpQuestions);
  const [reportDescriptions, setReportDescriptions] = useState<Record<string, string>>(strings.reportDescriptions);
  const [shortReportDescriptions, setShortReportDescriptions] = useState<Record<string, string>>(strings.shortReportDescriptions);
  const [overrideReportTypeForRequest, setOverrideReportTypeForRequest] = useState<string | null>(null);
  const [generatedContentMap, setGeneratedContentMap] = useState<{ [questionIndex: number]: { type: 'executive-summary' | 'presentation' | 'linkedin-post', index: number }[] }>({});
  const [isShareMode, setIsShareMode] = useState<boolean>(false);
  const [sharedArtifactType, setSharedArtifactType] = useState<'executive-summary' | 'presentation' | 'linkedin-post' | null>(null);
  const [lastShareLink, setLastShareLink] = useState<string | null>(null);

  // Track main question through follow-up conversations for context propagation
  const [previousMainQuestion, setPreviousMainQuestion] = useState<string | null>(null);

  // Track feature usage for analytics
  const [featureUsage, setFeatureUsage] = useState({
    presentation: false,
    executiveSummary: false,
    share: false,
    exploreCharts: false,
    deepDive: false,
    linkedinPost: false,
    expandReport: false as boolean | string
  });

  const trackFeatureUsage = useCallback((feature: keyof typeof featureUsage, value: any = true) => {
    setFeatureUsage(prev => ({ ...prev, [feature]: value }));
  }, []);

  // Use ref to access latest chat state in async functions without stale closures
  const chatRef = useRef<ChatMessage[]>(chat);

  useEffect(() => {
    chatRef.current = chat;
  }, [chat]);

  // Listen for chartDeleted event and update chat state with DOM's current HTML
  useEffect(() => {
    const handleChartDeleted = (event: Event) => {
      const customEvent = event as CustomEvent;
      const chartId = customEvent.detail?.chartId;
      const currentChat = chatRef.current;

      if (!chartId || currentChat.length === 0) return;

      const updatedChat = currentChat.map((msg) => {
        if (msg.role === 'assistant' && msg.content.includes(chartId)) {
          let content = msg.content;
          // Find the start of the div with this ID
          const startMarker = `id="${chartId}"`;
          const idIndex = content.indexOf(startMarker);

          if (idIndex !== -1) {
            // Find the actual start of this div tag (going backwards from id)
            const divStartIndex = content.lastIndexOf('<div', idIndex);

            if (divStartIndex !== -1) {
              // Robust approach: count div tags from divStartIndex
              // We want to find the matching closing div for the div at divStartIndex
              let openDivs = 0;
              let searchFrom = divStartIndex;
              let foundEnd = false;
              let endIndex = -1;

              do {
                const closeIndex = content.indexOf('</div>', searchFrom);
                const openIndex = content.indexOf('<div', searchFrom === divStartIndex ? searchFrom + 1 : searchFrom);

                if (closeIndex === -1) break; // formatting error - no closing tag

                // Check if we found an opening tag before the next closing tag
                if (openIndex !== -1 && openIndex < closeIndex) {
                  openDivs++;
                  searchFrom = openIndex + 1;
                } else {
                  // Found a closing tag
                  if (openDivs === 0) {
                    endIndex = closeIndex + 6; // length of </div>
                    foundEnd = true;
                    break;
                  }
                  openDivs--;
                  // Prevent negative depth (shouldn't happen in well-formed HTML starting from a valid div)
                  if (openDivs < 0) openDivs = 0;
                  searchFrom = closeIndex + 1;
                }
              } while (searchFrom < content.length);

              if (foundEnd) {
                const newContent = content.substring(0, divStartIndex) + content.substring(endIndex);
                return { ...msg, content: newContent };
              }
            }
          }
        }
        return msg;
      });

      setChat(updatedChat);
    };

    window.addEventListener('chartDeleted', handleChartDeleted);
    return () => window.removeEventListener('chartDeleted', handleChartDeleted);
  }, []);

  // Listen for analyticsChartDeleted event and remove the analytics JSON block
  useEffect(() => {
    const handleAnalyticsChartDeleted = (event: Event) => {
      const customEvent = event as CustomEvent;
      const originalMatch = customEvent.detail?.originalMatch;
      const currentChat = chatRef.current;

      if (!originalMatch || currentChat.length === 0) return;

      const updatedChat = currentChat.map((msg) => {
        if (msg.role === 'assistant' && msg.content.includes(originalMatch)) {
          const newContent = msg.content.replace(originalMatch, '');
          return { ...msg, content: newContent };
        }
        return msg;
      });

      setChat(updatedChat);
    };

    window.addEventListener('analyticsChartDeleted', handleAnalyticsChartDeleted);
    return () => window.removeEventListener('analyticsChartDeleted', handleAnalyticsChartDeleted);
  }, []);

  useEffect(() => {
    latestFetchInputSuggestionsControllerRef.current = fetchInputSuggestionsController;
  }, [fetchInputSuggestionsController]);

  useEffect(() => {
    latestFetchEnhanceSuggestionsControllerRef.current = fetchEnhanceSuggestionsController;
  }, [fetchEnhanceSuggestionsController]);

  const state: ResearchContextState = useMemo(() => ({
    chat: chat || [],
    message,
    latestMessage,
    model,
    longReportType,
    typing,
    streaming,
    expandingTyping,
    stop: stop || false,
    autocomplete,
    showConversationHistory,
    showMoreClicked,
    diveDeeperClicked,
    imagesLoading,
    images,
    conversationHistory,
    followUpQuestions,
    inputSuggestions,
    enhanceSuggestions,
    showEnhanceOptions,
    enhanceLoading,
    timestamp,
    error,
    controller,
    fetchInputSuggestionsController,
    fetchEnhanceSuggestionsController,
    myDataCheckBox,
    liteDataCheckBox,
    longReportCheckBox,
    shortReportCheckBox,
    modelDataCheckBox,
    customReportCheckBox,
    startMyDataDate,
    endMyDataDate,
    startLiteDataDate,
    endLiteDataDate,
    userDateOverride,
    systemPrompt,
    reportPrompt,
    keywordPrompt,
    searchQuery,
    searchResults,
    searchLoading,
    searchFilters,
    defaultFollowUpQuestions,
    reportFollowUpQuestions,
    reportDescriptions,
    shortReportDescriptions,
    routerReportType,
    historyLoading,
    isFuqLoading,
    presentationStatusMessage,
    overrideReportTypeForRequest,
    generatedContentMap,
    isShareMode,
    sharedArtifactType,
    previousMainQuestion,
  }), [
    chat,
    message,
    latestMessage,
    model,
    longReportType,
    typing,
    streaming,
    expandingTyping,
    stop,
    autocomplete,
    showConversationHistory,
    showMoreClicked,
    diveDeeperClicked,
    imagesLoading,
    images,
    conversationHistory,
    followUpQuestions,
    inputSuggestions,
    enhanceSuggestions,
    showEnhanceOptions,
    enhanceLoading,
    timestamp,
    error,
    controller,
    fetchInputSuggestionsController,
    fetchEnhanceSuggestionsController,
    myDataCheckBox,
    liteDataCheckBox,
    longReportCheckBox,
    shortReportCheckBox,
    modelDataCheckBox,
    customReportCheckBox,
    startMyDataDate,
    endMyDataDate,
    startLiteDataDate,
    endLiteDataDate,
    userDateOverride,
    systemPrompt,
    reportPrompt,
    keywordPrompt,
    searchQuery,
    searchResults,
    searchLoading,
    searchFilters,
    defaultFollowUpQuestions,
    reportFollowUpQuestions,
    reportDescriptions,
    shortReportDescriptions,
    routerReportType,
    historyLoading,
    isFuqLoading,
    presentationStatusMessage,
    overrideReportTypeForRequest,
    generatedContentMap,
    isShareMode,
    sharedArtifactType,
    previousMainQuestion,
  ]);

  const fetchInputSuggestions = useCallback(async (text: string) => {
    if (text.length === 0) return;

    if (latestFetchInputSuggestionsControllerRef.current) {
      latestFetchInputSuggestionsControllerRef.current.abort();
    }

    const newController = new AbortController();
    setFetchInputSuggestionsController(newController);

    try {
      const response = await fetch(`${HOST}/api/nlp/chat/input`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ input: text }),
        signal: newController.signal
      });

      const data = await response.json();
      setInputSuggestions(data);
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Error fetchInputSuggestions', error);
        setError('Failed to fetch input suggestions');
      }
    }
  }, []);

  const debouncedFetchInputSuggestions = useCallback(
    debounce((text: string) => {
      fetchInputSuggestions(text);
    }, 300),
    [fetchInputSuggestions]
  );

  const fetchEnhanceSuggestions = useCallback(async (text: string) => {
    if (text.trim().length === 0) return;

    if (latestFetchEnhanceSuggestionsControllerRef.current) {
      latestFetchEnhanceSuggestionsControllerRef.current.abort();
    }

    const newController = new AbortController();
    setFetchEnhanceSuggestionsController(newController);
    setEnhanceLoading(true);

    try {
      const response = await fetch(`${HOST}/api/nlp/chat/enhance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ input: text }),
        signal: newController.signal
      });

      const data = await response.json();
      setEnhanceSuggestions(data);
      // Only open the dropdown if there's actually something to show - an
      // empty/failed response (backend falls back to []) should look like
      // nothing happened, not like a dropdown stuck open with no options.
      setShowEnhanceOptions(response.ok && Array.isArray(data) && data.length > 0);
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Error fetchEnhanceSuggestions', error);
        setError('Failed to fetch enhanced questions');
        setShowEnhanceOptions(false);
      }
    } finally {
      // A superseded (aborted) request's own finally shouldn't clear the
      // loading flag the newer request just set.
      if (latestFetchEnhanceSuggestionsControllerRef.current === newController) {
        setEnhanceLoading(false);
      }
    }
  }, []);

  const saveChat = useCallback(async (questions: FollowUpQuestionContent, customChat?: ChatMessage[], customImages?: Record<string, string>, contentType?: 'presentation' | 'executive-summary' | 'linkedin-post', shareUrl?: string) => {
    try {
      // Use customChat if provided, otherwise use the latest chat from ref
      const chatToSave = customChat || chatRef.current;

      // console.log('saveChat called:', { contentType, chatLength: chatToSave?.length, hasCustomChat: !!customChat });


      // Calculate derived data for flat structure
      const userDataStr = localStorage.getItem('userData');
      const parsedUserData = userDataStr ? JSON.parse(userDataStr) : {};
      const userEmail = parsedUserData.email || '';

      const isCustomer = (() => {
        const userType = parsedUserData.userType || 'demo';
        const isInternal = userEmail.endsWith('@enkiai.com') || userEmail.endsWith('@askenki.ai');
        const isDemoOrSales = userType === 'demo' || userEmail.includes('sales');
        return !isInternal && !isDemoOrSales && userEmail !== '';
      })();

      const content = {
        presentation: null as string | null,
        executiveSummary: null as string | null,
        linkedinPost: null as string | null
      };

      // Extract content based on generatedContentMap - ONLY for the current question flow
      if (generatedContentMap) {
        // Find the index of the latest user message in the chat being saved
        const lastUserIdx = [...chatToSave].map((m, i) => ({ role: m.role, i })).reverse().find(m => m.role === 'user')?.i ?? -1;

        Object.entries(generatedContentMap).forEach(([qIdx, items]) => {
          const questionIndex = parseInt(qIdx);
          // Only take content if it belongs to the current user question we are looking at
          if (questionIndex === lastUserIdx) {
            (items as any[]).forEach(item => {
              if (item.type === 'presentation' && chatToSave[item.index]) {
                content.presentation = chatToSave[item.index].content;
              } else if (item.type === 'executive-summary' && chatToSave[item.index]) {
                content.executiveSummary = chatToSave[item.index].content;
              } else if (item.type === 'linkedin-post' && chatToSave[item.index]) {
                content.linkedinPost = chatToSave[item.index].content;
              }
            });
          }
        });
      }

      const payload = {
        chat: chatToSave, // Required for chatController.js

        // Core Fields aligned with DB schema
        timestamp: timestamp, // Required for share link lookup
        date: new Date(timestamp).toISOString(),
        user: userEmail,
        type: longReportType,
        startDate: startLiteDataDate,
        endDate: endLiteDataDate,
        question: [...chatToSave].reverse().find(m => m.role === 'user')?.content || '',
        // Answer should ALWAYS be the original report answer (the FIRST assistant message after the LAST user question)
        // This logic is the same for both artifact saving and normal chat saving
        // We find the last user message, then get the FIRST assistant message that follows it
        // This ensures we always get the original report, not any subsequent artifacts
        answer: (() => {
          // Use mainChat for artifact saving, chatToSave for normal saving
          const targetChat = contentType ? chatRef.current : chatToSave;
          // Find the index of the last user message
          const lastUserIndex = targetChat.map((m, i) => ({ role: m.role, i })).reverse().find(m => m.role === 'user')?.i ?? -1;
          // Find the first assistant message AFTER the last user message - this is the original answer
          const originalAnswer = targetChat.find((m, i) => i > lastUserIndex && m.role === 'assistant');
          return originalAnswer?.content || '';
        })(),

        followUpQuestions: questions?.followUpQuestions?.length > 0 ? questions : followUpQuestions,
        images: customImages || images || {},

        // New Data Quality Metrics - for artifacts, use main chat to get URL stats from original user message
        totalUrlsBeforeFilter: [...(contentType ? chatRef.current : chatToSave)].reverse().find(m => m.role === 'user')?.totalUrlsBeforeFilter || 0,
        totalUrlsAfterFilter: [...(contentType ? chatRef.current : chatToSave)].reverse().find(m => m.role === 'user')?.totalUrlsAfterFilter || 0,
        usedUrlsCount: (() => {
          const userMsg = [...chatToSave].reverse().find(m => m.role === 'user');
          if (userMsg?.sources?.length) return userMsg.sources.length;
          // Fallback: count unique URLs from the response when sources array is empty
          const assistantMsg = [...chatToSave].reverse().find(m => m.role === 'assistant');
          const urlMatches = assistantMsg?.content?.match(/href=['"]https?:\/\/[^'"]+['"]/g) || [];
          return new Set(urlMatches).size;
        })(),
        isCustomer: isCustomer,

        // Flattened Feature Usage
        // When contentType is provided, ONLY that specific artifact's flag should be true
        // Otherwise, check if content exists from generatedContentMap
        hasPresentation: (contentType ? contentType === 'presentation' : !!content.presentation) ? 'TRUE' : 'FALSE',
        hasExecutiveSummary: (contentType ? contentType === 'executive-summary' : !!content.executiveSummary) ? 'TRUE' : 'FALSE',
        hasLinkedinPost: (contentType ? contentType === 'linkedin-post' : !!content.linkedinPost) ? 'TRUE' : 'FALSE',
        hasShare: shareUrl ? 'TRUE' : 'FALSE',
        hasExploreCharts: (customImages && Object.keys(customImages).length > 0) ? 'TRUE' : 'FALSE',
        hasDeepDive: featureUsage.deepDive ? 'TRUE' : 'FALSE',
        hasExpandReport: featureUsage.expandReport ? String(featureUsage.expandReport).toUpperCase() : 'FALSE',

        // Flattened Feature Content
        // When contentType is provided, ONLY that specific artifact's content should be saved
        // Strip the question header (<h3>...</h3><br/>) from artifact content before saving
        contentPresentation: contentType === 'presentation' && customChat ? (customChat[customChat.length - 1]?.content || '').replace(/^<h3>.*?<\/h3><br\/?>/i, '') : (contentType ? null : content.presentation),
        contentExecutiveSummary: contentType === 'executive-summary' && customChat ? (customChat[customChat.length - 1]?.content || '').replace(/^<h3>.*?<\/h3><br\/?>/i, '') : (contentType ? null : content.executiveSummary),
        contentLinkedinPost: contentType === 'linkedin-post' && customChat ? (customChat[customChat.length - 1]?.content || '').replace(/^<h3>.*?<\/h3><br\/?>/i, '') : (contentType ? null : content.linkedinPost),
        shareLink: shareUrl || null
      };

      // console.log('Saving Chat Payload:', payload);

      await fetch(`${HOST}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', addToken: true as any },
        body: JSON.stringify(payload)
      });

      // Also send to userInput for CSV reporting (Andy's format)
      // Sanitize text fields to prevent || delimiter conflicts in CSV parsing
      const sanitize = (val: any) => typeof val === 'string' ? val.replace(/\|\|/g, '|') : val;

      const inputValue = [
        payload.date,
        payload.user,
        payload.type,
        sanitize(payload.question),
        sanitize(payload.answer),
        payload.startDate,
        payload.endDate,
        payload.isCustomer,
        payload.totalUrlsBeforeFilter,
        payload.totalUrlsAfterFilter,
        payload.usedUrlsCount,
        payload.hasPresentation,
        payload.hasExecutiveSummary,
        payload.hasLinkedinPost,
        payload.hasShare,
        payload.hasExploreCharts,
        payload.hasDeepDive,
        payload.hasExpandReport,
        payload.shareLink || '',
        sanitize(payload.contentPresentation || ''),
        sanitize(payload.contentExecutiveSummary || ''),
        sanitize(payload.contentLinkedinPost || ''),
        sanitize(JSON.stringify(payload.images || {})),
        sanitize(JSON.stringify(payload.followUpQuestions || {}))
      ].join('||');

      // console.log('UserInput Payload:', { inputType: 'chat', inputValueLength: inputValue.length });

      // Fire-and-forget with delay: allow server to recover after chat save
      setTimeout(() => {
        fetch(`${HOST}/api/userInput`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ inputType: 'chat', inputValue })
        }).catch(() => { });
      }, 3000);

    } catch (error) {
      console.error('Error saving chat:', error);
      setError('Failed to save chat');
    }
  }, [timestamp, endLiteDataDate, startLiteDataDate, longReportType, followUpQuestions, images]);

  const onShareClick = (idx: number) => {
    trackFeatureUsage('share');
    if (isShareMode) {
      new ClipboardService(document).copyText(window.location.href);
      return;
    }

    const sharePromise = (async () => {
      // Use same pattern as shareArtifact
      const shareTimestamp = Date.now();
      const currentChat = chatRef.current;

      // Get Q&A pair
      const userMsg = idx > 0 ? currentChat[idx - 1] : [...currentChat].reverse().find(m => m.role === 'user');
      const assistantMsg = currentChat[idx] || currentChat[currentChat.length - 1];

      // Get images for this message to include in share content
      const assistantIdx = currentChat[idx] ? idx : currentChat.length - 1;
      const assistantImages = images[assistantIdx] || '';

      const miniChat: ChatMessage[] = [
        userMsg || { role: 'user', content: '' } as ChatMessage,
        {
          ...(assistantMsg || { role: 'assistant', content: '' }),
          content: assistantImages ? `${assistantMsg.content}<br/>${assistantImages}` : assistantMsg.content
        } as ChatMessage
      ];

      // POST with chat array (same as shareArtifact)
      await fetch(`${HOST}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', addToken: true as any },
        body: JSON.stringify({
          chat: miniChat,
          timestamp: shareTimestamp,
          details: { endLiteDataDate, startLiteDataDate, longReportType, followUpQuestions }
        })
      });

      // GET with idx=1 (same as shareArtifact)
      const shareResponse = await fetch(`${HOST}/api/chat?timestamp=${shareTimestamp}&idx=1`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', addToken: true as any }
      });
      const shareResult = await shareResponse.json();

      const location = getWindowLocation();
      const shareUrl = `${location}/share?c=${shareResult.id}`;
      return shareUrl;
    })();

    new ClipboardService(document).copyText(sharePromise);

    sharePromise.then(shareUrl => {
      setLastShareLink(shareUrl);
      // Short delay to allow server to complete share link creation
      setTimeout(() => saveChat(followUpQuestions, undefined, undefined, undefined, shareUrl).catch(() => { }), 500);
    }).catch(error => {
      console.error('Error creating share link:', error);
    });
  };

  // Share artifact with isolated mini-chat (only question + artifact)
  const shareArtifact = useCallback(async (artifactContent: string, questionContent: string, sources?: any[], artifactType?: 'executive-summary' | 'presentation' | 'linkedin-post') => {
    const artifactPromise = (async () => {
      // Create a unique timestamp for this artifact share
      const artifactTimestamp = Date.now();

      // Get URL stats from the original user message in the main chat
      const originalUserMessage = [...chatRef.current].reverse().find(m => m.role === 'user');
      const totalUrlsBeforeFilter = originalUserMessage?.totalUrlsBeforeFilter || 0;
      const totalUrlsAfterFilter = originalUserMessage?.totalUrlsAfterFilter || 0;

      // Create mini chat with just the question and artifact
      // Sources are stored in the USER message (same as normal chat behavior)
      // Preserve URL stats from the original user message
      const miniChat: ChatMessage[] = [
        { role: 'user', content: questionContent, sources: sources || [], totalUrlsBeforeFilter, totalUrlsAfterFilter },
        { role: 'assistant', content: artifactContent, sources: sources || [] }
      ];


      // Save the mini chat to backend
      await fetch(`${HOST}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', addToken: true as any },
        body: JSON.stringify({
          chat: miniChat,
          timestamp: artifactTimestamp,
          details: {
            endLiteDataDate,
            startLiteDataDate,
            longReportType,
            followUpQuestions: followUpQuestions, // Include current follow-up questions for share page
            artifactType: artifactType || null // Save artifact type for share page
          }
        })
      });

      // Get share ID for the mini chat (idx=1 is the artifact, which is the second message)
      const shareResponse = await fetch(`${HOST}/api/chat?timestamp=${artifactTimestamp}&idx=1`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', addToken: true as any }
      });
      const shareResult = await shareResponse.json();

      // Copy share link to clipboard
      const location = getWindowLocation();
      const shareUrl = `${location}/share?c=${shareResult.id}`;
      return { shareUrl, miniChat };
    })();

    new ClipboardService(document).copyText(artifactPromise.then(res => res.shareUrl));

    artifactPromise.then(({ shareUrl, miniChat }) => {
      setLastShareLink(shareUrl);
      // Short delay to allow server to complete share link creation
      setTimeout(() => saveChat(followUpQuestions, miniChat, undefined, artifactType, shareUrl), 500);
      trackFeatureUsage('share');
    }).catch(error => {
      console.error('Error sharing artifact:', error);
      const toastData = {
        title: "Failed to create share link",
        iconName: "error",
        iconColor: "red",
        duration: 3000,
      };
      dispatch(pushToastMessage(toastData));
    });
  }, [endLiteDataDate, startLiteDataDate, longReportType, dispatch, followUpQuestions]);

  const generateFollowUpQuestions = useCallback(async () => {
    const newController = new AbortController();
    setController(newController);
    setFUQLoading(true);

    try {
      const response = await fetch(`${HOST}/api/nlp/followUp`, {
        method: 'POST',
        signal: newController.signal,
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ text: latestMessage })
      });

      const questions = await response.json();
      if (questions && questions.followUpQuestions) {
        setFollowUpQuestions(questions);
        saveChat(questions);
      } else {
        setFollowUpQuestions({
          followUpQuestions: [],
          hasError: true,
          errorMessage: 'There is a problem getting follow up questions, click here to try again.'
        } as FollowUpQuestionContent);
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        setStreaming(false);
        setStop(false);
        return;
      }

      console.error('Error generating follow-up questions', error);
      setFollowUpQuestions({
        followUpQuestions: [],
        hasError: true,
        errorMessage: 'There is a problem getting follow up questions, click here to try again.'
      } as FollowUpQuestionContent);
    } finally {
      setFUQLoading(false);
    }
  }, [saveChat, chat]);

  // Helper function to handle streaming responses for various report types
  const processStreamingResponse = async (
    endpoint: string,
    contentType: 'linkedin-post' | 'executive-summary' | 'presentation',
    statusMessageText: string,
    targetQuestionIndex: number,
    initialChatLength: number,
    lastUserMessage: string
  ) => {
    try {
      // Don't show typing spinner, only streaming
      setTyping(false);
      setStreaming(true);
      setStop(false);

      // Update map with new content type
      if (targetQuestionIndex !== -1) {
        setGeneratedContentMap(prev => ({
          ...prev,
          [targetQuestionIndex]: [...(prev[targetQuestionIndex] || []), { type: contentType, index: initialChatLength }]
        }));
      }

      // Create AbortController
      const newController = new AbortController();
      setController(newController);

      // Show first status message immediately
      setPresentationStatusMessage(statusMessageText);

      // Add empty assistant message IMMEDIATELY so the status message can be shown
      setChat(prev => [...prev, {
        role: "assistant",
        content: ''
      }]);

      // Get user type from localStorage or fetch it
      const userData = localStorage.getItem('userData');
      const parsedUserData = userData ? JSON.parse(userData) : {};
      const userType = parsedUserData.userType || 'demo';

      // Only send the conversation up through the question this was
      // triggered for — not the whole live chat — so the backend's "last
      // assistant message" always resolves to the answer for that question,
      // not whatever is most recent when this fires.
      const scopedChat = targetQuestionIndex !== -1
        ? chatRef.current.slice(0, targetQuestionIndex + 2)
        : chatRef.current;

      // Request body
      const body: any = {
        chat: scopedChat,
        user: parsedUserData.email || '',
        userType: userType
      };

      // Add liteData for presentation (needed for Explore Charts)
      if (contentType === 'presentation') {
        body.liteData = liteDataCheckBox.value;
      }

      const response = await fetch(`${HOST}${endpoint}`, {
        method: 'POST',
        signal: newController.signal,
        headers: { 'Content-Type': 'application/json', addToken: true as any },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || `Failed to generate ${contentType.replace('-', ' ')}` };
        }
        throw new Error(errorData.error || `Failed to generate ${contentType.replace('-', ' ')}`);
      }

      // Handle streaming response
      const reader = response.body?.pipeThrough(new TextDecoderStream()).getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      let generatedContent = '';
      let buffer = '';
      let hasRealContent = false;
      let lastStatusMessage = '';

      while (true) {
        // Check if stop was requested
        if (stopRef.current) {
          reader.cancel();
          newController.abort();
          break;
        }

        const { value, done } = await reader.read();
        if (done) break;

        // Check again after reading
        if (stopRef.current) {
          break;
        }

        if (value) {
          buffer += value;

          // Check if we have a complete status message (typing-message spans)
          const statusMatch = buffer.match(/<span class="typing-message">([^<]+)<\/span>/);
          if (statusMatch && !hasRealContent) {
            const newStatusMessage = statusMatch[1];
            // Only update if it's a different message (to show them sequentially)
            if (newStatusMessage !== lastStatusMessage) {
              lastStatusMessage = newStatusMessage;
              setPresentationStatusMessage(newStatusMessage);
            }
            // Remove complete status message from buffer
            buffer = buffer.replace(/<span class="typing-message">[^<]+<\/span>\s*\n*\n*/g, '');
          }

          // Check if real content has started (HTML tags that are not status messages)
          // Real content starts when we see HTML tags like <div, <h2, <p, etc. (not just typing-message)
          const hasRealHTML = buffer.match(/<(div|h[1-6]|p|strong|a|br|ul|ol|li|img|hr)[\s>]/i);
          if (hasRealHTML && !hasRealContent) {
            hasRealContent = true;
            setPresentationStatusMessage(''); // Clear status message when real content starts
            // Scroll to bottom when real content starts appearing
            const historyContainer = document.querySelector('.research__form__history-container') as HTMLElement;
            if (historyContainer) {
              historyContainer.scrollTo({ top: historyContainer.scrollHeight, behavior: 'auto' });
            }
          }

          // If we have real content, add it to generatedContent
          if (hasRealContent) {
            generatedContent += buffer;
            buffer = '';

            // Update the last message with streaming content
            setChat(prev => {
              const newChat = [...prev];
              if (newChat.length > 0 && newChat[newChat.length - 1].role === 'assistant') {
                newChat[newChat.length - 1] = {
                  ...newChat[newChat.length - 1],
                  content: mdToHTML(generatedContent, false)
                };
              }
              return newChat;
            });
          }
        }
      }

      // Process any remaining buffer
      if (buffer.trim()) {
        if (hasRealContent) {
          generatedContent += buffer;
          setChat(prev => {
            const newChat = [...prev];
            if (newChat.length > 0 && newChat[newChat.length - 1].role === 'assistant') {
              newChat[newChat.length - 1] = {
                ...newChat[newChat.length - 1],
                content: mdToHTML(generatedContent, false)
              };
            }
            return newChat;
          });
        }
      }

      // Clear status message at the end
      setPresentationStatusMessage('');

      // Save chat with the new content
      // Use chatRef.current to get the latest state
      let updatedChat = [...chatRef.current];

      // Add header to content
      const contentWithHeader = lastUserMessage ? `<h3>${lastUserMessage}</h3><br/>${mdToHTML(generatedContent, false)}` : mdToHTML(generatedContent, false);

      // Handle race condition: check if ref has updated length yet
      if (updatedChat.length === initialChatLength) {
        // Ref is stale, append new message
        updatedChat.push({ role: 'assistant', content: contentWithHeader, sources: [] } as ChatMessage);
      } else {
        // Ref updated, update last message
        if (updatedChat.length > 0 && updatedChat[updatedChat.length - 1].role === 'assistant') {
          updatedChat[updatedChat.length - 1] = {
            ...updatedChat[updatedChat.length - 1],
            content: contentWithHeader,
            sources: []
          };
        }
      }

      saveChat(followUpQuestions, updatedChat, undefined, contentType);

      setTyping(false);
      setStreaming(false);
      setStop(false);
    } catch (error: any) {
      // Check if error is due to abort
      if (error.name === 'AbortError' || stopRef.current) {
        setPresentationStatusMessage('');
        setTyping(false);
        setStreaming(false);
        setStop(false);
        return;
      }

      console.error(`Error generating ${contentType}:`, error);
      setTyping(false);
      setStreaming(false);
      setStop(false);

      const errorMessage = error.message || `Failed to generate ${contentType.replace('-', ' ')}. Please try again.`;
      setChat(prev => [...prev, {
        role: "assistant",
        content: `<p style="color: var(--danger-color-500);">${errorMessage}</p>`
      }]);

      const toastData = {
        title: errorMessage,
        iconName: "error",
        iconColor: "red",
        duration: 5000,
      };
      dispatch(pushToastMessage(toastData));
    }
  };

  const onReportFollowUpClick = useCallback(async (message: string, questionIndex?: number) => {
    if (!message) return;

    // Capture start length to handle race conditions in saveChat later
    const initialChatLength = chatRef.current.length;

    // Reset feature usage for the NEW question session
    setFeatureUsage({
      presentation: false,
      executiveSummary: false,
      share: false,
      exploreCharts: false,
      deepDive: false,
      linkedinPost: false,
      expandReport: false
    });

    // Determine target question index: use provided, or find last user message
    const targetQuestionIndex = questionIndex ?? [...chatRef.current].map((m, i) => ({ ...m, i })).reverse().find(m => m.role === 'user')?.i ?? -1;

    // Find the last user message to use as header
    const lastUserMessage = [...chatRef.current].reverse().find(m => m.role === 'user')?.content || '';

    // Check request type
    if (message.toLowerCase().includes('create linkedin post')) {
      trackFeatureUsage('linkedinPost');
      await processStreamingResponse(
        '/api/analysis/linkedin-post',
        'linkedin-post',
        'Generating LinkedIn post...',
        targetQuestionIndex,
        initialChatLength,
        lastUserMessage
      );
    } else if (message.toLowerCase() === 'executive summary' || message.toLowerCase().includes('executive summary')) {
      trackFeatureUsage('executiveSummary');
      await processStreamingResponse(
        '/api/analysis/executive-summary',
        'executive-summary',
        'Generating executive summary...',
        targetQuestionIndex,
        initialChatLength,
        lastUserMessage
      );
    } else if (message.toLowerCase() === 'presentation' || message.toLowerCase().includes('presentation')) {
      trackFeatureUsage('presentation');
      await processStreamingResponse(
        '/api/analysis/presentation',
        'presentation',
        'Generating presentation slides...',
        targetQuestionIndex,
        initialChatLength,
        lastUserMessage
      );
    } else if (message.toLowerCase().includes('swot')) {
      try {
        setTyping(true);
        setStreaming(true);

        const userData = localStorage.getItem('userData');
        const parsedUserData = userData ? JSON.parse(userData) : {};
        const userType = parsedUserData.userType || 'demo';

        const response = await fetch(`${HOST}/api/analysis/swot`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', addToken: true as any },
          body: JSON.stringify({
            chat: chat,
            user: parsedUserData.email || '',
            userType: userType,
            liteDataDate: [startLiteDataDate, endLiteDataDate],
            myDataDate: [startMyDataDate, endMyDataDate]
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to generate SWOT table');
        }

        const data = await response.json();
        const swot = data.swot;
        // Remove extra newlines and whitespace from HTML
        const htmlContent = mdToHTML(swot.trim().replace(/\n+/g, ' '), false);

        setChat(prev => [...prev, {
          role: "assistant",
          content: htmlContent
        }]);

        setTyping(false);
        setStreaming(false);
      } catch (error: any) {
        console.error('Error generating SWOT table:', error);
        setTyping(false);
        setStreaming(false);

        const errorMessage = error.message || 'Failed to generate SWOT table. Please try again.';
        setChat(prev => [...prev, {
          role: "assistant",
          content: `<p style="color: var(--danger-color-500);">${errorMessage}</p>`
        }]);

        const toastData = {
          title: errorMessage,
          iconName: "error",
          iconColor: "red",
          duration: 5000,
        };
        dispatch(pushToastMessage(toastData));
      }
    } else if (message.toLowerCase().includes('pestl')) {
      try {
        setTyping(true);
        setStreaming(true);

        const userData = localStorage.getItem('userData');
        const parsedUserData = userData ? JSON.parse(userData) : {};
        const userType = parsedUserData.userType || 'demo';

        const response = await fetch(`${HOST}/api/analysis/pestl`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', addToken: true as any },
          body: JSON.stringify({
            chat: chat,
            user: parsedUserData.email || '',
            userType: userType,
            liteDataDate: [startLiteDataDate, endLiteDataDate],
            myDataDate: [startMyDataDate, endMyDataDate]
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to generate PESTL table');
        }

        const data = await response.json();
        const pestl = data.pestl;
        // Remove extra newlines and whitespace from HTML
        const htmlContent = mdToHTML(pestl.trim().replace(/\n+/g, ' '), false);

        setChat(prev => [...prev, {
          role: "assistant",
          content: htmlContent
        }]);

        setTyping(false);
        setStreaming(false);
      } catch (error: any) {
        console.error('Error generating PESTL table:', error);
        setTyping(false);
        setStreaming(false);

        const errorMessage = error.message || 'Failed to generate PESTL table. Please try again.';
        setChat(prev => [...prev, {
          role: "assistant",
          content: `<p style="color: var(--danger-color-500);">${errorMessage}</p>`
        }]);

        const toastData = {
          title: errorMessage,
          iconName: "error",
          iconColor: "red",
          duration: 5000,
        };
        dispatch(pushToastMessage(toastData));
      }
    } else {
      // Check if this is a SWOT or PESTL highlight request
      const isSwotHighlight = message.toLowerCase().includes('highlight strengths only') ||
        message.toLowerCase().includes('highlight weaknesses only') ||
        message.toLowerCase().includes('highlight opportunities only') ||
        message.toLowerCase().includes('highlight threats only');

      const isPestlHighlight = message.toLowerCase().includes('dive deeper into political factors') ||
        message.toLowerCase().includes('dive deeper into economic factors') ||
        message.toLowerCase().includes('dive deeper into social factors') ||
        message.toLowerCase().includes('dive deeper into technological factors') ||
        message.toLowerCase().includes('dive deeper into legal & regulatory factors') ||
        message.toLowerCase().includes('dive deeper into legal and regulatory factors');

      if (isSwotHighlight || isPestlHighlight) {
        trackFeatureUsage('expandReport', message);
        trackFeatureUsage('exploreCharts', false);
        // Find the original question from chat (first user message that's not a highlight request)
        const originalQuestion = chat
          .filter(msg => msg.role === 'user')
          .reverse()
          .find(msg => !msg.content.toLowerCase().includes('highlight') &&
            !msg.content.toLowerCase().includes('dive deeper') &&
            !msg.content.toLowerCase().includes('expand on'))?.content ||
          chat.find(msg => msg.role === 'user')?.content ||
          '';

        // Create enhanced message: original question + highlight instruction
        // The createPrompt function will parse this and extract the highlight instruction
        const enhancedMessage = originalQuestion ? `${originalQuestion}\n\n${message}` : message;

        // Set override flag for In-Depth Analysis style response without changing dropdown
        setOverrideReportTypeForRequest('researchReport');

        // Set the enhanced message and send
        setMessage(enhancedMessage);
        setLatestMessage(enhancedMessage);

        // Send message and clear override flag after sending
        setTimeout(() => {
          sendUserMessage();
          // Clear override after sending
          setTimeout(() => {
            setOverrideReportTypeForRequest(null);
          }, 100);
        }, 100);
      } else {
        // Normal expand report flow - track for Business Blueprint, Installation/Cancellation Analysis
        trackFeatureUsage('expandReport', message);
        setMessage(message);
        setLatestMessage(message);
        setTimeout(() => sendUserMessage(), 500);
      }
    }
  }, [chat, dispatch, sendUserMessage, longReportType, setLongReportType]);

  const addImages = useCallback(() => {
    setImagesLoading(true);
    const currentChat = chat;
    const idx = currentChat.length - 1;
    const controller = new AbortController();
    setController(controller);

    fetch(`${HOST}/api/nlp/images`, {
      method: 'POST',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({
        chat: currentChat,
        liteData: liteDataCheckBox.value,
        debug: false
      })
    }).then(response => response.text())
      .then(data => {
        setImagesLoading(false);
        const newImages = {
          ...images,
          [idx]: data
        };
        setImages(newImages);
        saveChat(followUpQuestions, undefined, newImages);
      }).catch(e => {
        setImagesLoading(false);
        console.error('Error generating chart', e);
      });
  }, [liteDataCheckBox.value, chat, images, followUpQuestions, saveChat]);

  const onExploreChartClick = useCallback(() => {
    trackFeatureUsage('exploreCharts');
    trackFeatureUsage('expandReport', false);
    addImages();
  }, [addImages, trackFeatureUsage]);

  const loadChat = useCallback(async (shareId?: string, localTimestamp?: number) => {
    if (!timestamp && !localTimestamp && !shareId) return;

    if (latestControllerRef.current) {
      latestControllerRef.current.abort();
    }

    const params = shareId ? `shareId=${shareId}` : `timestamp=${localTimestamp ?? timestamp}`;

    try {
      const response = await fetch(`${HOST}/api/chat?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', addToken: true as any }
      });

      const chatData = await response.json();

      if (typeof chatData === 'object' && "chat" in chatData) {
        setChat(chatData.chat);
        setShowMoreClicked(false);
        setImages({});
        setTyping(false);
        setStreaming(false);
        setExpandingTyping(false);
        setImagesLoading(false);
        setFollowUpQuestions({} as FollowUpQuestionContent);

        if ("details" in chatData && chatData.chat.length - 1 in chatData.details) {
          const details = chatData.details[chatData.chat.length - 1];
          if (details.endLiteDataDate) setEndLiteDataDate(details.endLiteDataDate);
          if (details.startLiteDataDate) setStartLiteDataDate(details.startLiteDataDate);
          if (details.longReportType) setLongReportType(details.longReportType);

          const { followUpQuestions } = details;
          if (followUpQuestions) {
            if (isCategorizedFollowUpQuestions(followUpQuestions)) {
              setFollowUpQuestions(followUpQuestions);
            } else {
              const categorizedQuestions = convertFlatArrayToFollowUpQuestions(followUpQuestions);
              setFollowUpQuestions(categorizedQuestions);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading chat:', error);
      setError('Failed to load chat');
    }
  }, [timestamp]);

  const getConversationHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const response = await fetch(`${HOST}/api/chat`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', addToken: true as any }
      });

      const conversationHistoryData = await response.json();
      setConversationHistory(conversationHistoryData);
    } catch (error) {
      console.error('Error getting conversation history:', error);
      setError('Failed to get conversation history');
    } finally {
      setHistoryLoading(false);
    }
  }, [setHistoryLoading]);

  const deleteConversation = useCallback(async (timestampToDelete: string) => {
    try {
      await fetch(`${HOST}/api/chat?timestamp=${timestampToDelete}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', addToken: true as any }
      });

      const updatedHistory = conversationHistory.filter(
        conversation => conversation.timestamp !== parseInt(timestampToDelete)
      );
      setConversationHistory(updatedHistory);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      setError('Failed to delete conversation');
    }
  }, [conversationHistory]);

  const actions: ResearchContextActions = useMemo(() => ({
    setChatMessage: (message: string) => {
      setMessage(message);
      setLatestMessage(message);
      setAutocomplete(true);
      debouncedFetchInputSuggestions(message);
    },
    setSendUserMessage: (sendUserMessage: () => void) => {
      setSendUserMessage(sendUserMessage);
    },
    addChatMessage: (message: ChatMessage) => setChat(prev => [...(prev || []), message]),
    updateLastChatMessage: (content: string) => {
      setChat(prev => {
        if (!prev || prev.length === 0) return prev || [];
        const updatedChat = [...prev];
        updatedChat[updatedChat.length - 1] = {
          ...updatedChat[updatedChat.length - 1],
          content: content
        };
        return updatedChat;
      });
    },
    clearChat: () => {
      setChat([]);
      setImages({});
      setFollowUpQuestions({} as FollowUpQuestionContent);
      setShowMoreClicked(false);
      setDiveDeeperClicked(false);
      setTyping(false);
      setStreaming(false);
      setExpandingTyping(false);
      setImagesLoading(false);
      setShowEnhanceOptions(false);
      setEnhanceSuggestions([]);
      setEnhanceLoading(false);
    },

    setTyping,
    setStreaming,
    setStop,
    setAutocomplete,
    setShowConversationHistory,
    setShowMoreClicked,
    setImagesLoading,
    onReportFollowUpClick,
    shareArtifact,
    onShareClick,
    setExpandingTyping,
    setImages,
    setChat,
    addImage: (index: string, imageData: string) => {
      setImages(prevImages => ({
        ...prevImages,
        [index]: imageData
      }));
    },
    setConversationHistory,
    setFollowUpQuestions,
    setInputSuggestions,
    setLongReportType,
    setIsShareMode,
    setSharedArtifactType,

    updateCheckbox: (checkboxName: keyof ResearchContextState, updates: Partial<CheckboxState>) => {
      if (checkboxName === 'myDataCheckBox') {
        setMyDataCheckBox(prev => ({ ...prev, ...updates }));
      } else if (checkboxName === 'liteDataCheckBox') {
        setLiteDataCheckBox(prev => ({ ...prev, ...updates }));
      } else if (checkboxName === 'longReportCheckBox') {
        setLongReportCheckBox(prev => ({ ...prev, ...updates }));
      } else if (checkboxName === 'shortReportCheckBox') {
        setShortReportCheckBox(prev => ({ ...prev, ...updates }));
      } else if (checkboxName === 'modelDataCheckBox') {
        setModelDataCheckBox(prev => ({ ...prev, ...updates }));
      } else if (checkboxName === 'customReportCheckBox') {
        setCustomReportCheckBox(prev => ({ ...prev, ...updates }));
      }
    },

    setDateRange: (type: 'myData' | 'liteData', startDate: string, endDate: string) => {
      if (type === 'myData') {
        setStartMyDataDate(startDate);
        setEndMyDataDate(endDate);
      } else {
        setStartLiteDataDate(startDate);
        setEndLiteDataDate(endDate);
      }
    },

    setSystemPrompt,
    setReportPrompt,
    setKeywordPrompt,
    setModel,

    setSearchQuery,
    setSearchResults,
    setSearchLoading,
    setSearchFilters,
    updateSearchFilter: (key: string, value: any) => {
      setSearchFilters(prevFilters => ({
        ...prevFilters,
        [key]: value
      }));
    },
    setMultiple: (data: Partial<ResearchContextState>) => {
      Object.entries(data).forEach(([key, value]) => {
        if (value === undefined) return;
        switch (key) {
          case 'chat': setChat(value as ChatMessage[]); break;
          case 'timestamp': setTimestamp(value as number); break;
          case 'message': setMessage(value as string); break;
          case 'latestMessage': setLatestMessage(value as string); break;
          case 'typing': setTyping(value as boolean); break;
          case 'streaming': setStreaming(value as boolean); break;
          case 'expandingTyping': setExpandingTyping(value as boolean); break;
          case 'stop': setStop(value as boolean); break;
          case 'autocomplete': setAutocomplete(value as boolean); break;
          case 'showConversationHistory': setShowConversationHistory(value as boolean); break;
          case 'showMoreClicked': setShowMoreClicked(value as boolean); break;
          case 'diveDeeperClicked':
            if (value === true) trackFeatureUsage('deepDive');
            setDiveDeeperClicked(value as boolean);
            break;
          case 'imagesLoading': setImagesLoading(value as boolean); break;
          case 'images': setImages(value as Record<string, string>); break;
          case 'conversationHistory': setConversationHistory(value as any[]); break;
          case 'followUpQuestions': setFollowUpQuestions(value as FollowUpQuestionContent); break;
          case 'inputSuggestions': setInputSuggestions(value as string[]); break;
          case 'enhanceSuggestions': setEnhanceSuggestions(value as string[]); break;
          case 'showEnhanceOptions': setShowEnhanceOptions(value as boolean); break;
          case 'enhanceLoading': setEnhanceLoading(value as boolean); break;
          case 'longReportType': setLongReportType(value as string); break;
          case 'model': setModel(value as string); break;
          case 'searchQuery': setSearchQuery(value as string); break;
          case 'searchResults': setSearchResults(value as any[]); break;
          case 'searchLoading': setSearchLoading(value as boolean); break;
          case 'searchFilters': setSearchFilters(value as Record<string, any>); break;
          case 'error': setError(value as string | null); break;
          case 'controller': setController(value as AbortController | null); break;
          case 'fetchInputSuggestionsController': setFetchInputSuggestionsController(value as AbortController | null); break;
          case 'fetchEnhanceSuggestionsController': setFetchEnhanceSuggestionsController(value as AbortController | null); break;
          case 'startLiteDataDate': setStartLiteDataDate(value as string); break;
          case 'endLiteDataDate': setEndLiteDataDate(value as string); break;
          case 'userDateOverride': setUserDateOverride(value as boolean); break;
          case 'startMyDataDate': setStartMyDataDate(value as string); break;
          case 'endMyDataDate': setEndMyDataDate(value as string); break;
          case 'systemPrompt': setSystemPrompt(value as string); break;
          case 'reportPrompt': setReportPrompt(value as string); break;
          case 'keywordPrompt': setKeywordPrompt(value as string); break;
          case 'previousMainQuestion': setPreviousMainQuestion(value as string | null); break;
        }
      });
    },
    setDiveDeeperClicked: (val: boolean) => {
      if (val) trackFeatureUsage('deepDive');
      setDiveDeeperClicked(val);
    },
    onExploreChartClick,
    setError,

    setController,
    abortCurrentRequest: () => {
      if (latestControllerRef.current) {
        latestControllerRef.current.abort();
        setController(null);
      }
    },

    fetchInputSuggestions: debouncedFetchInputSuggestions,
    fetchEnhanceSuggestions,
    generateFollowUpQuestions,
    loadChat,
    getConversationHistory,
    deleteConversation,
    setOnExpandResearchClick,
    onExpandResearchClick,

    resetState: () => {
      setChat([]);
      setMessage('');
      setLatestMessage('');
      setTyping(false);
      setStreaming(false);
      setExpandingTyping(false);
      setStop(false);
      setAutocomplete(true);
      setShowConversationHistory(false);
      setShowMoreClicked(false);
      setDiveDeeperClicked(false);
      setImagesLoading(false);
      setFeatureUsage({
        presentation: false,
        executiveSummary: false,
        share: false,
        exploreCharts: false,
        deepDive: false,
        linkedinPost: false,
        expandReport: false
      });
      setImages({});
      setConversationHistory([]);
      setFollowUpQuestions({ followUpQuestions: [{ category: 'General', questions: strings.followUpQuestions }] });
      setInputSuggestions([]);
      setEnhanceSuggestions([]);
      setShowEnhanceOptions(false);
      setEnhanceLoading(false);
      setLongReportType('researchReport');
      setModel('');
      setSearchQuery('');
      setSearchResults([]);
      setSearchLoading(false);
      setSearchFilters({});
      setError(null);
      setController(null);
      setFetchInputSuggestionsController(null);
      setMyDataCheckBox({ id: 'myData', text: 'My Data', value: false, color: 'blue' });
      setLiteDataCheckBox({ id: 'liteData', text: 'Lite Data', value: true, color: 'green' });
      setLongReportCheckBox({ id: 'longReport', text: 'Long Report', value: false, color: 'purple' });
      setShortReportCheckBox({ id: 'shortReport', text: 'Short Report', value: true, color: 'orange' });
      setModelDataCheckBox({ id: 'modelData', text: 'Model Data', value: false, color: 'red' });
      setCustomReportCheckBox({ id: 'customReport', text: 'Custom Report', value: false, color: 'indigo' });
      setStartMyDataDate('2020-01-01');
      setEndMyDataDate(new Date().toISOString().split('T')[0]);
      setStartLiteDataDate('2020-01-01');
      setEndLiteDataDate(new Date().toISOString().split('T')[0]);
      setUserDateOverride(false);
      setSystemPrompt('');
      setReportPrompt('');
      setKeywordPrompt('');
      setDefaultFollowUpQuestions(strings.defaultFollowUpQuestions);
      setReportFollowUpQuestions(strings.reportFollowUpQuestions);
      setReportDescriptions(strings.reportDescriptions);
      setShortReportDescriptions(strings.shortReportDescriptions);
      setTimestamp(Date.now());
    },
    resetSearchState: () => {
      setSearchQuery('');
      setSearchResults([]);
      setSearchLoading(false);
      setSearchFilters({});
      setError(null);
    },
    scrollToBottom: () => {
      const historyContainer = document.querySelector('.research__form__history-container') as HTMLElement;
      if (historyContainer) {
        historyContainer.scrollTo({
          top: historyContainer.scrollHeight,
          behavior: 'auto'
        });
      }
    },
    setRouterReportType,
    trackFeatureUsage,
  }), [
    debouncedFetchInputSuggestions, fetchEnhanceSuggestions, generateFollowUpQuestions, saveChat, loadChat,
    getConversationHistory, deleteConversation,
    conversationHistory, setRouterReportType, trackFeatureUsage, isShareMode
  ]);

  const value = useMemo(() => ({
    state,
    actions
  }), [state, actions]);

  return (
    <ResearchContext.Provider value={value}>
      {children}
    </ResearchContext.Provider>
  );
};

export const useResearchContext = () => {
  const context = useContext(ResearchContext);
  if (context === undefined) {
    throw new Error('useResearchContext must be used within a ResearchContextProvider');
  }
  return context;
};

export function withResearchContext<P extends object>(
  Component: React.ComponentType<P & { researchContext: ReturnType<typeof useResearchContext> }>
) {
  return function WithResearchContextComponent(props: P) {
    const researchContext = useResearchContext();
    return <Component {...props} researchContext={researchContext} />;
  };
}

export const ResearchContextProvider = React.memo(ResearchContextProviderComponent);

ResearchContextProvider.displayName = 'ResearchContextProvider';

export default ResearchContextProvider;
